// Công Pháp `character.arts` (UI_SPEC §18.1): công pháp chủ tu. Số chính là thanh Tâm Đắc tới tầng
// kế; Tam Nguyên là hướng tu (chip), lên tầng là hành động bấm tay có giá (CULTIVATION §6.1).
import type { JSX } from 'preact';
import { t } from '@i18n/t';
import { formatFull } from '@i18n/format';
import { realmLabel } from '@i18n/stage';
import { techniqueDef, TECHNIQUE_MAX_LAYER } from '@data/content/techniques';
import { layerBlock, layerCap, layerCost } from '@sim/cultivation';
import { cultivation, raiseLayer } from '@state/modules/cultivation';
import { wallet } from '@state/modules/wallet';
import { NumBar } from '../../kit/NumBar';
import { TextButton } from '../../kit/TextButton';
import { Icon } from '../../icons';

const TRI = ['body', 'spirit', 'mind'] as const;

export function ArtsScreen(): JSX.Element {
  const c = cultivation.value;
  const tech = c.technique;
  const def = techniqueDef(tech.mainId);
  const stones = wallet.value.spiritStone;
  const next = Math.min(tech.layer + 1, TECHNIQUE_MAX_LAYER);
  const cost = layerCost(next);
  const block = layerBlock(c, stones);
  const capped = block === 'realmCap' || block === 'maxLayer';
  const reason =
    block === 'mastery'
      ? t('ui.arts.block.mastery')
      : block === 'stones'
        ? t('ui.arts.block.stones', { n: formatFull(cost.stones - stones) })
        : block === 'realmCap'
          ? t('ui.arts.block.realmCap', { realm: realmLabel(c.step), n: layerCap(c) })
          : block === 'maxLayer'
            ? t('ui.arts.block.maxLayer')
            : null;
  return (
    <>
      <div class="ht-caption" data-id="caption">
        <b data-id="arts-title">{t('ui.arts.caption', { name: t(`${def.id}.name`), n: tech.layer })}</b>
        <span>
          {t('ui.arts.sub', {
            grade: t(`grade.${def.grade}`),
            element: t(def.element ? `element.${def.element}` : 'element.none'),
          })}
        </span>
      </div>
      <div class="arts-slot q-hoang" aria-hidden="true">
        <Icon name="cultivation" />
      </div>
      {capped ? (
        <p class="arts-full ht-chip gold" data-id="arts-full">
          {t('ui.arts.full')}
        </p>
      ) : (
        <NumBar
          id="arts-bar"
          class="arts-bar"
          value={tech.mastery}
          max={cost.mastery}
          ariaLabel={t('ui.arts.bar', {
            c: formatFull(Math.floor(tech.mastery)),
            max: formatFull(cost.mastery),
          })}
        />
      )}
      <div class="arts-tri" data-id="arts-tri">
        {TRI.map((k) => (
          <span class="ht-chip" key={k}>
            {t('ui.arts.tri', { name: t(`cult.triOrigin.${k}`), n: Math.floor(tech.triOrigin[k] + 1e-9) })}
          </span>
        ))}
        <span class="ht-chip dim">
          {t('ui.arts.ratio', { body: def.ratio.body, spirit: def.ratio.spirit, mind: def.ratio.mind })}
        </span>
      </div>
      <div class="arts-perks">
        {def.layerPerks.map((p) => (
          <div class={tech.layer >= p.layer ? 'ht-statline on' : 'ht-statline dim'} key={p.layer}>
            <span>
              {t('ui.arts.perk.masteryMult', { layer: p.layer, pct: Math.round(p.effect.value * 100) })}
            </span>
          </div>
        ))}
      </div>
      <div class="arts-actions">
        <div class="ht-actions">
          <TextButton id="arts-raise" disabled={block !== null} onClick={() => void raiseLayer()}>
            {t('ui.arts.raise')}
          </TextButton>
          {!capped && (
            <span class="ht-chip price" style={{ '--cur-rgb': 'var(--cur-spiritStone-rgb)' }}>
              <Icon name="spiritStone" />
              {formatFull(cost.stones)}
            </span>
          )}
          <span class="ht-chip price wallet" style={{ '--cur-rgb': 'var(--cur-spiritStone-rgb)' }}>
            <Icon name="spiritStone" />
            {formatFull(stones)}
          </span>
        </div>
        {reason && (
          <p class={capped ? 'ht-reason dead' : 'ht-reason'} data-id="arts-reason">
            {reason}
          </p>
        )}
      </div>
    </>
  );
}
